import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";

import { QueryKeys } from "constants/query-keys";
import { ToastContent, getToastContent } from "constants/toasts";
import { ApiManager } from "services/api";
import { deserializeMacro, deserializeMacros } from "services/api/macros/macro-serializer";
import { selectMaxPrivateMacrosPerAgent, selectMaxSharedMacros } from "store/entities/license";
import { ICreateMacroPayload, IMacro, IMacroApiResponse, IUpdateMacroPayload } from "store/entities/macros/interfaces";
import { ToastVariant, ToastsActions } from "store/features/toasts";

import { queryClient } from "../../queryClient";

/* 
  This hook is used to fetch, create, update, and delete macros.
  It uses react-query to cache the data and handle loading states.
  It also uses redux to dispatch toast messages when a macro is created, updated, or deleted.
  
  @returns {
    macros: IMacro[],
    selectMacro: (id: string) => IMacro | undefined,
    createMacro: useMutation<ICreateMacroPayload, IMacroApiResponse>,
    updateMacro: useMutation<IUpdateMacroPayload, IMacroApiResponse>,
    deleteMacro: useMutation<string, void>,
    isLoading: boolean,
  }

  @example - using this hook in a component:
  const { macros, selectMacro, createMacro, updateMacro, deleteMacro, isLoading } = useMacros();
  const macro = selectMacro({ id: '123' });
  createMacro.mutate({ name: 'My Macro', content: 'My Macro Content' });
  updateMacro.mutate({ id: '123', name: 'My Macro', content: 'My Macro Content' });
  deleteMacro.mutate({ id: '123' });

  @example - getting loading status while calling a macro mutation:
  const { createMacro } = useMacros();
  const { isLoading } = createMacro;
  createMacro.mutate({ name: 'My Macro', content: 'My Macro Content' });
  if (isLoading) {
    return <div>Loading...</div>;
  }

*/

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const fetchMacros = async () => {
  const { result, error } = await ApiManager.macrosApi.fetchAll();
  if (error) {
    throw error;
  }
  return deserializeMacros(result);
};

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const prefetchMacros = async () => {
  await queryClient.prefetchQuery({
    queryKey: [QueryKeys.Macros],
    queryFn: fetchMacros,
  });
};

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useMacros = () => {
  const dispatch = useDispatch();
  const maxSharedMacros = useSelector(selectMaxSharedMacros);
  const maxPrivateMacrosPerAgent = useSelector(selectMaxPrivateMacrosPerAgent);

  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery<IMacro[]>({
    queryKey: [QueryKeys.Macros],
    queryFn: fetchMacros,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
  const selectMacro = ({ id }: { id: string }) => data?.find((macro: any) => macro.id === id);

  const createMacro = useMutation({
    // @ts-expect-error TS2769
    mutationFn: async ({ params }: { params: ICreateMacroPayload }) => {
      const { result, error } = await ApiManager.macrosApi.create(params);
      if (error) {
        dispatch(
          ToastsActions.createToast({
            variant: ToastVariant.Error,
            content: getToastContent(ToastContent.MACRO_CREATE_FAILURE, {
              type: error.error.type,
              message: error.error.message,
              maxSharedMacros,
              maxPrivateMacrosPerAgent,
            }),
          })
        );
        throw error;
      }
      return result;
    },
    onSuccess: (macro: IMacroApiResponse) => {
      // @ts-expect-error TS2345
      queryClient.setQueryData([QueryKeys.Macros], (prev: IMacro[]) => [...prev, deserializeMacro(macro)]);
      dispatch(
        ToastsActions.createToast({
          variant: ToastVariant.Success,
          content: getToastContent(ToastContent.MACRO_CREATE_SUCCESS),
        })
      );
    },
  });

  const updateMacro = useMutation({
    // @ts-expect-error TS2769
    mutationFn: async ({ id, params }: { id: string; params: IUpdateMacroPayload }) => {
      const { result, error } = await ApiManager.macrosApi.update(id, params);
      if (error) {
        dispatch(
          ToastsActions.createToast({
            variant: ToastVariant.Error,
            content: getToastContent(ToastContent.MACRO_UPDATE_FAILURE),
          })
        );
        throw error;
      }
      return result;
    },
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onSuccess: (macro: IMacroApiResponse, variables: any) => {
      // @ts-expect-error TS2345
      queryClient.setQueryData([QueryKeys.Macros], (prev: IMacro[]) =>
        prev.map(updatedMacro => (updatedMacro.id === variables.id ? deserializeMacro(macro) : updatedMacro))
      );
      dispatch(
        ToastsActions.createToast({
          variant: ToastVariant.Success,
          content: getToastContent(ToastContent.MACRO_UPDATE_SUCCESS),
        })
      );
    },
  });

  const deleteMacro = useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const { result, error } = await ApiManager.macrosApi.remove(id);
      if (!result) {
        dispatch(
          ToastsActions.createToast({
            variant: ToastVariant.Error,
            content: getToastContent(ToastContent.MACRO_DELETE_FAILURE),
          })
        );
        throw error;
      }
      return result;
    },
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onSuccess: (_: any, variables: any) => {
      // @ts-expect-error TS2345
      queryClient.setQueryData([QueryKeys.Macros], (prev: IMacro[]) =>
        prev.filter(deletedMacro => deletedMacro.id !== variables.id)
      );
      dispatch(
        ToastsActions.createToast({
          variant: ToastVariant.Success,
          content: getToastContent(ToastContent.MACRO_DELETE_SUCCESS),
        })
      );
    },
  });

  return {
    macros: isLoading ? [] : data,
    // FIXME: Fix this the next time the file is edited.
    // @ts-expect-error TS18048
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    privateMacros: isLoading ? [] : data.filter((macro: any) => macro.agentId !== null),
    // FIXME: Fix this the next time the file is edited.
    // @ts-expect-error TS18048
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    sharedMacros: isLoading ? [] : data.filter((macro: any) => macro.agentId === null),
    selectMacro,
    createMacro,
    updateMacro,
    deleteMacro,
    isLoading,
  };
};
